/**
 * Tenant vs landlord portal routing (mirrors backend portal-auth.utils).
 */

import { getAdminAppBaseUrl, mapAdminPathToAdminSpa } from './adminAppUrl';

export type PortalSessionUser = {
  role?: string | null;
  tenantProfileId?: string | null;
  mustVerifyPhone?: boolean;
  createdById?: string | null;
  aud?: string;
};

export function isTenantSession(user: PortalSessionUser | null | undefined): boolean {
  if (!user) return false;
  return user.role === 'TENANT' || !!user.tenantProfileId;
}

export function isPlatformAdminSession(user: PortalSessionUser | null | undefined): boolean {
  if (!user) return false;
  if (user.aud === 'tenantplus-platform-admin') return true;
  if (user.role === 'PLATFORM_ADMIN') return true;
  if (user.role === 'ADMIN' && (user.createdById == null || user.createdById === undefined)) {
    return true;
  }
  return false;
}

/** Where an authenticated tenant should land (verify-phone or portal). */
export function resolveTenantPortalNavigation(
  user: PortalSessionUser | null | undefined
): string {
  if (user?.mustVerifyPhone) {
    return '/tenant/verify-phone';
  }
  if (user?.tenantProfileId) {
    return `/tenant/portal/${user.tenantProfileId}`;
  }
  return '/login?as=tenant';
}

/** Landlord-app paths that tenants must never use as post-login targets. */
export function isLandlordAppPath(pathname: string | undefined | null): boolean {
  if (!pathname) return false;
  if (pathname.startsWith('/tenant')) return false;

  const publicExact = new Set([
    '/login',
    '/signup',
    '/landing',
    '/forgot-password',
    '/reset-password',
    '/terms',
    '/privacy',
    '/accept-invitation',
    '/onboarding',
    '/debug',
    '/force-change-password',
    '/signup-pending-approval',
    '/verify-phone',
  ]);
  if (publicExact.has(pathname)) return false;

  const publicPrefixes = [
    '/deposit-payment/',
    '/verify-payment/',
    '/pay/',
    '/location-pin/',
    '/tenant/',
  ];
  if (publicPrefixes.some((p) => pathname.startsWith(p))) return false;
  if (pathname.startsWith('/admin')) return false;

  return (
    pathname === '/' ||
    pathname.startsWith('/properties') ||
    pathname.startsWith('/tenants') ||
    pathname.startsWith('/payments') ||
    pathname.startsWith('/messages') ||
    pathname.startsWith('/billing') ||
    pathname.startsWith('/test')
  );
}

export function resolvePostLoginPath(
  user: PortalSessionUser | null | undefined,
  redirectPath?: string | null
): string {
  if (isTenantSession(user)) {
    return resolveTenantPortalNavigation(user);
  }
  if (isPlatformAdminSession(user)) {
    const adminBase = getAdminAppBaseUrl();
    if (adminBase) {
      const path =
        redirectPath?.startsWith('/admin')
          ? mapAdminPathToAdminSpa(redirectPath)
          : '/';
      // Absolute URL — LoginPage should assign when it starts with http
      return `${adminBase}${path === '/' ? '' : path}`;
    }
    if (redirectPath?.startsWith('/admin')) return redirectPath;
    return '/admin';
  }
  if (redirectPath && redirectPath !== '/login' && !redirectPath.startsWith('/tenant')) {
    return redirectPath;
  }
  return '/';
}
